/* eslint-disable react-hooks/exhaustive-deps */
import { createContext, useContext, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  getUserPageAccess,
  userHaveActivePlan,
} from "../services/PlanServices/PlanServices";
import { useLocation } from "react-router-dom";
import { doesUserHavePermissionToAccessPage } from "../helpers/helpers";
import useGlobalMarketMetrics from "../hooks/useGlobalMarketMetrics";
import {
  PROTECTED_PAGE_ACCESS_ALLOWED,
  PROTECTED_PAGE_ACCESS_DENIED,
  PROTECTED_PAGE_ACCESS_PENDING,
} from "../components/plans/protectedPageAccess";

const globalContext = createContext({
  sidebarClose: true,
  loading: false,
  sideDrawerIsOpen: false,
  isNavbarOpenInMobile: true,
  assetPairCoin: "",
  isActive: null,
  doesUserHavePermission: null,
  accessState: PROTECTED_PAGE_ACCESS_PENDING,
  isSuccess: false,
  pageAccess: [],
  setSidebarClose: () => {},
  setLoading: () => {},
  setSideDrawerIsOpen: () => {},
  setIsNavbarOpenInMobile: () => {},
  setAssetPairCoin: () => {},
  globalMarketMetrics: null,
  globalMarketMetricsLoading: false,
  globalMarketMetricsFetching: false,
  globalMarketMetricsError: null,
  refetchGlobalMarketMetrics: () => Promise.resolve(),
});

export const GlobalProvider = ({ children }) => {
  const [sidebarClose, setSidebarClose] = useState(true);
  const [loading, setLoading] = useState(true);
  const [sideDrawerIsOpen, setSideDrawerIsOpen] = useState(false);
  const [isNavbarOpenInMobile, setIsNavbarOpenInMobile] = useState(true);
  const [assetPairCoin, setAssetPairCoin] = useState("");

  const { pathname } = useLocation();
  const metricsEnabled = !!pathname && !["/signin", "/signup"].includes(pathname);

  const {
    marketMetrics: globalMarketMetrics,
    isLoading: globalMarketMetricsLoading,
    isFetching: globalMarketMetricsFetching,
    error: globalMarketMetricsError,
    refetch: refetchGlobalMarketMetrics,
  } = useGlobalMarketMetrics({ enabled: metricsEnabled });

  // ✅ FIX 1: first segment (handles /notification/create correctly)
  const firstSegment =
    pathname.split("/").filter(Boolean)[0] || "beginner";

  // ✅ FIX 2: canonical permission keys (route -> permission key)
  // تصمیم شما: canonical = plural
  const PAGE_KEY_MAP = {
    notification: "notifications",
  };

  const currentPageKey = PAGE_KEY_MAP[firstSegment] || firstSegment;

  const {
    data,
    isSuccess,
    isLoading: activePlanLoading,
  } = useQuery({
    queryFn: userHaveActivePlan,
    queryKey: ["active-plan"],
    staleTime: 1000 * 60 * 5,
    refetchOnMount: false,
    enabled: !!pathname && !["/signin", "/signup"].includes(pathname),
    refetchOnWindowFocus: true,
  });

  // ✅ FIX: حتی وقتی is_active=false هم باید user-access fetch شود
  // چون بک‌اند در no-plan حالت default_pages را برمی‌گرداند.
  const {
    data: pageAccess,
    isLoading,
  } = useQuery({
    queryFn: getUserPageAccess,
    queryKey: ["user-access", data?.is_active], // ✅ وقتی پلن عوض شد، دوباره fetch شود
    refetchOnMount: false,
    enabled:
      !!pathname &&
      !["/signin", "/signup"].includes(pathname) &&
      !activePlanLoading,
    refetchOnWindowFocus: true,
  });

  const accessCheckPending = activePlanLoading || isLoading || data === undefined;
  const doesUserHavePermission = accessCheckPending
    ? null
    : doesUserHavePermissionToAccessPage(pageAccess, currentPageKey) === true;
  const accessState = accessCheckPending
    ? PROTECTED_PAGE_ACCESS_PENDING
    : doesUserHavePermission
    ? PROTECTED_PAGE_ACCESS_ALLOWED
    : PROTECTED_PAGE_ACCESS_DENIED;

  useEffect(() => {
    if (!isLoading && !activePlanLoading) {
      setLoading(false);
    }
  }, [isLoading, activePlanLoading]);

  const globalValue = {
    setIsNavbarOpenInMobile,
    setSideDrawerIsOpen,
    setAssetPairCoin,
    setSidebarClose,
    setLoading,
    sidebarClose,
    loading,
    sideDrawerIsOpen,
    assetPairCoin,
    isNavbarOpenInMobile,
    isActive: data ? data?.is_active : null,
    pageAccess,
    isSuccess,
    doesUserHavePermission,
    accessState,
    globalMarketMetrics,
    globalMarketMetricsLoading,
    globalMarketMetricsFetching,
    globalMarketMetricsError,
    refetchGlobalMarketMetrics,
  };

  return (
    <globalContext.Provider value={globalValue}>
      {children}
    </globalContext.Provider>
  );
};

export default globalContext;

export const useGlobalContext = () => {
  const context = useContext(globalContext);
  if (context === undefined) {
    throw new Error("useGlobalContext must be used within a GlobalProvider");
  }
  return context;
};
